import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { ToastService } from '../../services/toast.service';
import { ConfirmService } from '../../services/confirm.service';
import { Preventivo } from '../../models';

@Component({
  selector: 'app-preventivi',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="page-container">
      <div class="header-flex">
        <div>
          <h1>Gestione Preventivi</h1>
          <p class="subtitle">Elenco preventivi emessi progressivi per anno</p>
        </div>
        <a routerLink="/preventivi/nuovo" class="btn-primary">
          <span class="material-icons" style="font-size: 18px;">add_circle</span> Nuovo Preventivo
        </a>
      </div>

      <!-- Search & Filter Bar -->
      <div class="search-bar card" style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
        <div style="display: flex; align-items: center; gap: 8px; flex: 1; min-width: 220px;">
          <span class="material-icons search-icon">search</span>
          <input type="text" [(ngModel)]="searchTerm" placeholder="Cerca per numero, cliente o stato..." />
          <button *ngIf="searchTerm" class="btn-clear" (click)="searchTerm = ''">Cancella</button>
        </div>
        <div>
          <select [(ngModel)]="selectedYear" class="year-select" style="padding: 10px 14px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-main); color: var(--text-main); font-size: 14px; font-weight: 600;">
            <option value="">Tutti gli anni</option>
            <option *ngFor="let y of availableYears" [value]="y">{{ y }}</option>
          </select>
        </div>
      </div>

      <!-- Desktop Table View -->
      <div class="card table-card desktop-only">
        <table class="data-table">
          <thead>
            <tr>
              <th>Numero</th>
              <th>Cliente</th>
              <th>Data</th>
              <th>Stato</th>
              <th>Totale</th>
              <th style="text-align: right;">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let p of filteredPreventivi">
              <td><strong>{{ p.numero || ('#' + p.id) }}</strong></td>
              <td>{{ p.cognome }} {{ p.nome }}</td>
              <td>{{ p.data | date:'dd/MM/yyyy' }}</td>
              <td>
                <span class="badge" [ngClass]="'badge-' + p.stato">{{ p.stato }}</span>
              </td>
              <td><strong>€ {{ p.importo_totale | number:'1.2-2' }}</strong></td>
              <td style="text-align: right;">
                <a [routerLink]="['/preventivi', p.id]" class="btn-sm" title="Visualizza Dettagli">
                  <span class="material-icons" style="font-size: 16px;">visibility</span>
                </a>
                <button class="btn-sm btn-danger" (click)="deletePreventivo(p.id!)" title="Elimina">
                  <span class="material-icons" style="font-size: 16px;">delete</span>
                </button>
              </td>
            </tr>
            <tr *ngIf="filteredPreventivi.length === 0">
              <td colspan="6" class="text-center">Nessun preventivo trovato.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card View -->
      <div class="mobile-only mobile-card-list">
        <div class="mobile-item-card card" *ngFor="let p of filteredPreventivi">
          <div class="mobile-card-header">
            <strong>{{ p.numero || ('Preventivo #' + p.id) }}</strong>
            <span class="badge" [ngClass]="'badge-' + p.stato">{{ p.stato }}</span>
          </div>
          <div class="mobile-card-body">
            <p><strong>Cliente:</strong> {{ p.cognome }} {{ p.nome }}</p>
            <p><strong>Data:</strong> {{ p.data | date:'dd/MM/yyyy' }}</p>
            <p><strong>Totale:</strong> € {{ p.importo_totale | number:'1.2-2' }}</p>
          </div>
          <div class="mobile-card-footer">
            <a [routerLink]="['/preventivi', p.id]" class="btn-sm" style="width: auto; padding: 6px 12px;">Dettagli</a>
            <button class="btn-sm btn-danger" (click)="deletePreventivo(p.id!)" style="width: auto; padding: 6px 12px;">Elimina</button>
          </div>
        </div>
        <div class="text-center" *ngIf="filteredPreventivi.length === 0">Nessun preventivo trovato.</div>
      </div>
    </div>
  `,
  styles: [`
    .page-container { max-width: 1280px; margin: 0 auto; }
    .header-flex { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
    h1 { margin: 0; color: var(--text-main); font-size: 26px; font-weight: 700; letter-spacing: -0.5px; }
    .subtitle { color: var(--text-muted); margin: 4px 0 0; font-size: 14px; }
    
    .search-bar { display: flex; align-items: center; gap: 12px; padding: 12px 20px; margin-bottom: 24px; border-radius: 12px; }
    .search-bar .search-icon { color: var(--text-muted); }
    .search-bar input { border: none; background: transparent; font-size: 15px; width: 100%; color: var(--text-main); outline: none; }
    .btn-clear { background: var(--border); border: none; padding: 4px 10px; border-radius: 6px; cursor: pointer; font-size: 12px; color: var(--text-muted); }

    .table-card { padding: 0; overflow: hidden; border-radius: 12px; border: 1px solid var(--border); }
    .data-table { width: 100%; border-collapse: collapse; text-align: left; }
    .data-table th, .data-table td { padding: 16px 20px; border-bottom: 1px solid var(--border); font-size: 14px; }
    .data-table th { background: var(--bg-main); color: var(--text-muted); font-weight: 600; text-transform: uppercase; font-size: 12px; letter-spacing: 0.5px; }
    .data-table tbody tr { transition: background 0.15s; }
    .data-table tbody tr:hover { background: var(--bg-main); }
    
    .btn-primary { display: inline-flex; align-items: center; gap: 6px; background: var(--primary); color: white; border: none; padding: 10px 18px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 14px; transition: background 0.2s; box-shadow: 0 2px 4px rgba(37,99,235,0.2); }
    .btn-primary:hover { background: var(--primary-dark); }
    
    .btn-sm { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; border: 1px solid var(--border); background: var(--surface); color: var(--text-muted); text-decoration: none; cursor: pointer; transition: all 0.2s; margin-right: 6px; }
    .btn-sm:hover { background: var(--bg-main); color: var(--text-main); }
    .btn-danger { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.2); color: var(--danger); }
    .btn-danger:hover { background: var(--danger); color: white; }
    
    .text-center { text-align: center; color: var(--text-muted); padding: 40px !important; }

    /* Mobile Card List */
    .mobile-only { display: none !important; }
    .desktop-only { display: block !important; }
    .mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
    .mobile-item-card { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
    .mobile-card-header { display: flex; justify-content: space-between; align-items: center; }
    .mobile-card-body p { margin: 4px 0; font-size: 13px; color: var(--text-muted); }
    .mobile-card-footer { display: flex; gap: 8px; margin-top: 8px; }

    @media (max-width: 768px) {
      .desktop-only { display: none !important; }
      .mobile-only { display: flex !important; }
    }
  `]
})
export class PreventiviComponent implements OnInit {
  preventivi: Preventivo[] = [];
  searchTerm = '';
  selectedYear = '';

  constructor(
    private apiService: ApiService,
    private toastService: ToastService,
    private confirmService: ConfirmService
  ) {}

  ngOnInit() {
    this.loadPreventivi();
  }

  get availableYears(): string[] {
    const years = new Set<string>();
    this.preventivi.forEach(p => {
      if (p.data) {
        years.add(new Date(p.data).getFullYear().toString());
      }
    });
    return Array.from(years).sort().reverse();
  }

  get filteredPreventivi(): Preventivo[] {
    return this.preventivi.filter(p => {
      const matchYear = !this.selectedYear || (p.data && new Date(p.data).getFullYear().toString() === this.selectedYear);
      if (!matchYear) return false;
      if (!this.searchTerm.trim()) return true;
      const term = this.searchTerm.toLowerCase();
      return (
        p.numero?.toLowerCase().includes(term) ||
        p.id?.toString().includes(term) ||
        p.nome?.toLowerCase().includes(term) ||
        p.cognome?.toLowerCase().includes(term) ||
        p.stato?.toLowerCase().includes(term)
      );
    });
  }

  loadPreventivi() {
    this.apiService.getPreventivi().subscribe({
      next: (data) => this.preventivi = data,
      error: (err) => console.error('Errore caricamento preventivi', err)
    });
  }

  async deletePreventivo(id: number) {
    const confirmed = await this.confirmService.confirm(
      'Elimina Preventivo',
      'Sei sicuro di voler eliminare questo preventivo?',
      'Elimina',
      'Annulla',
      true
    );
    if (confirmed) {
      this.apiService.deletePreventivo(id).subscribe({
        next: () => {
          this.toastService.success('Preventivo eliminato con successo');
          this.loadPreventivi();
        },
        error: (err) => this.toastService.error(err.error?.error || 'Errore eliminazione preventivo')
      });
    }
  }
}
